import * as React from 'react';

import { Search_EntityType } from '__generated__/graphql-types';

import { Grid, Typography } from '@coursera/cds-core';
import { StyleSheet, breakPoint, css } from '@coursera/coursera-ui';

import { NUMBER_OF_SEARCH_RESULTS } from 'bundles/course-cards/constants';
import InPageNumberOfResultsSection from 'bundles/coursera-plus-common/components/search/InPageNumberOfResultsSection';
import type { InputProps as SearchResultsProps } from 'bundles/coursera-plus-common/components/search/InPageSearchResults';
import InPageSearchResults from 'bundles/coursera-plus-common/components/search/InPageSearchResults';
import { getCopy } from 'bundles/coursera-plus-common/utils/courseraPlusUtils';
import DomainChips from 'bundles/coursera-plus-landing-page/components/redesign/DomainChips';
import SearchBar from 'bundles/coursera-plus-landing-page/components/redesign/SearchBar';
import SearchCardsPlaceHolder from 'bundles/coursera-ui/components/search/SearchCardsPlaceHolder';
import { BASE_STYLES } from 'bundles/design-system/components/container-styles';
import { useUserAgent } from 'bundles/page/contexts/UserAgentApiContext';
import {
  ALL_PRODUCTS_INDEX_TYPE,
  COURSERA_PLUS_POPULAR_DOMAINS_MAPPING,
  SEARCH_MAIN_INDEX_ID,
} from 'bundles/search-common/constants';
import { useSearch } from 'bundles/search-common/providers/SearchContext';
import SearchProvider from 'bundles/search-common/providers/SearchProvider';
import type { SearchConfig } from 'bundles/search-common/providers/searchTypes';

import _t from 'i18n!nls/coursera-plus-landing-page';

const SEARCH_INPUT_LABEL_ID = 'courseraplus-search-title';

const styles = StyleSheet.create({
  containerWrapper: { ...BASE_STYLES.containerWrapper, overflow: 'hidden' },
  container: {
    margin: '0 auto',
    ...BASE_STYLES.container,
    padding: '64px 0',
    [`@media (max-width: ${breakPoint.md - 1}px)`]: {
      padding: '48px 0',
    },
  },
  searchContainer: {
    width: '100%',
    backgroundColor: 'var(--cds-color-white-0)',
  },
  searchDomains: {
    marginTop: '10px',
    textAlign: 'center',
    [`@media (max-width: ${breakPoint.md - 1}px)`]: {
      marginTop: '18px',
    },
  },
  searchTitle: {
    ...BASE_STYLES.title,
    fontSize: '2.125rem !important',
    marginBottom: '24px',
    [`@media (max-width: ${breakPoint.md - 1}px)`]: {
      ...BASE_STYLES.titleMobile,
      marginBottom: '22px',
    },
  },
  searchFormWrapper: {
    margin: '0 auto',
  },
  searchResults: {
    marginTop: '48px',
    [`@media (max-width: ${breakPoint.md - 1}px)`]: {
      marginTop: '24px',
    },
  },
});

type Props = {
  additionalSearchResultsProps?: Partial<SearchResultsProps>;
  ownsCourseraPlus?: boolean;
  numberOfProductsInPlusOverride?: string;
};

const CourseraPlusSearchContainer = ({ additionalSearchResultsProps, numberOfProductsInPlusOverride }: Props) => {
  const userAgent = useUserAgent();
  const { updateQuery, getSearchResults, isLoading } = useSearch();
  const updateSearchQueryToDomain = (domain: string) => {
    const domainAsSearchQuery = COURSERA_PLUS_POPULAR_DOMAINS_MAPPING.find(
      (currentDomain) => currentDomain.id === domain.toLowerCase()
    )?.name;

    if (domainAsSearchQuery) {
      updateQuery(domainAsSearchQuery);
    }
  };

  const searchResults = getSearchResults(SEARCH_MAIN_INDEX_ID);
  const currentSearchQuery = searchResults?.query || '';

  const { numberOfProducts: numberOfProductsInPlus } = getCopy();
  const numberOfProducts = numberOfProductsInPlusOverride ?? numberOfProductsInPlus;
  const sectionTitle = _t('Search #{numberOfProducts} learning programs', { numberOfProducts });

  return (
    <div {...css(styles.containerWrapper)}>
      <Grid className={css(styles.container).className}>
        <div {...css(styles.searchContainer)} data-e2e="search_results">
          <Typography variant="d2semibold" className={css(styles.searchTitle).className} id={SEARCH_INPUT_LABEL_ID}>
            {sectionTitle}
          </Typography>
          <Grid container>
            <Grid item xs={12}>
              <SearchBar
                currentSearchQuery={currentSearchQuery}
                placeholderText={_t('e.g. Machine Learning')}
                searchInputLabelId={SEARCH_INPUT_LABEL_ID}
              />
            </Grid>
            <Grid item xs={12} justifyContent="center" className={css(styles.searchDomains).className}>
              <DomainChips domains={COURSERA_PLUS_POPULAR_DOMAINS_MAPPING} updateDomain={updateSearchQueryToDomain} />
            </Grid>
          </Grid>
          {isLoading && <SearchCardsPlaceHolder count={NUMBER_OF_SEARCH_RESULTS} />}
          {currentSearchQuery && (
            <div {...css(styles.searchResults)}>
              <div className="tab-contents">
                <InPageNumberOfResultsSection
                  limitNumberOfResults={NUMBER_OF_SEARCH_RESULTS}
                  currentTab={ALL_PRODUCTS_INDEX_TYPE}
                />
                <InPageSearchResults
                  userAgent={userAgent}
                  currentTab={ALL_PRODUCTS_INDEX_TYPE}
                  currentSearchQuery={currentSearchQuery}
                  numberOfResults={NUMBER_OF_SEARCH_RESULTS}
                  {...(additionalSearchResultsProps ?? {})}
                />
              </div>
            </div>
          )}
        </div>
      </Grid>
    </div>
  );
};

const CourseraPlusSearchContainerWithData = (props: Props) => {
  const searchConfigs: SearchConfig[] = [
    {
      id: SEARCH_MAIN_INDEX_ID,
      entityType: Search_EntityType.Products,
      limit: NUMBER_OF_SEARCH_RESULTS,
      facetFilters: [['isPartOfCourseraPlus:true']], // Filter for Coursera Plus products
      syncQueryWithUrl: false,
      syncFiltersWithUrl: false,
      maxValuesPerFacet: 100,
    },
  ];

  return (
    <SearchProvider searchConfigs={searchConfigs} shouldReplaceRoute={false} ssr={false}>
      <CourseraPlusSearchContainer {...props} />
    </SearchProvider>
  );
};

export default CourseraPlusSearchContainerWithData;
